import { Injector, inject } from '@angular/core';
import { CanMatchFn, GuardResult, Router } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import { GarageService } from '@core/services/garage/garage.service';

// This guard ensures that the user is authenticated and has at least one garage loaded.
export const authGaragesGuard: CanMatchFn = async (_route, segments): Promise<GuardResult> => {
  const injector = inject(Injector);
  const router = inject(Router);
  const garageService = inject(GarageService);
  const { AuthService } = await import('@core/services/auth/auth.service');
  const authService = injector.get(AuthService);

  // Reflect cookie-based session without calling getSession on every navigation.
  await authService.ensureSessionFresh();

  if (!authService.isAuthenticated()) {
    const returnUrl = `/${segments
      .map((segment) => segment.path)
      .filter(Boolean)
      .join('/')}`;

    return router.createUrlTree(['/auth/login'], {
      queryParams: returnUrl !== '/' ? { returnUrl } : undefined
    });
  }

  if (garageService.garages().length > 0) {
    return true;
  }

  try {
    await lastValueFrom(garageService.loadGarages());
    return true;
  } catch (error) {
    const { SnackbarService } = await import('@core/services/snackbar/snackbar.service');
    const snackbarService = injector.get(SnackbarService);

    console.error('Failed to load garages:', error);
    snackbarService.error('Unable to load your garage. Please sign in again.');

    try {
      await authService.signOut();
    } catch {
      // Ignore sign-out failures and redirect anyway.
    }

    return router.createUrlTree(['/auth/login']);
  }
};
